import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  MousePointer2,
  Pause,
  Play,
  RotateCcw,
  Sparkles,
  Waves,
  X
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

type TourPhase = 'intro' | 'touring' | 'complete' | 'closed';

interface TourStep {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  narration: string;
  route?: string;
  selector?: string;
  actionSelector?: string;
  duration?: number;
}

interface SpotlightRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const FEMALE_VOICE_HINTS = [
  'joana',
  'catarina',
  'amália',
  'amalia',
  'helena',
  'luciana',
  'fernanda',
  'francisca',
  'samantha',
  'victoria',
  'female',
  'mulher',
  'maria'
];

const NATURAL_VOICE_HINTS = [
  'natural',
  'neural',
  'premium',
  'enhanced',
  'online'
];

const STEP_BREATHING_PAUSE_MS = 1450;

const TOUR_STEPS: TourStep[] = [
  {
    id: 'scope',
    eyebrow: '01 · ADN operacional',
    title: 'Uma operação, um contexto vivo',
    body: 'Cliente, espécie, fase, instalação e tanque mantêm toda a inteligência alinhada com a realidade biológica.',
    narration: 'Bem-vindo ao AQL Vision. Começamos pelo ADN operacional. Cliente, espécie, fase, instalação e tanque. Tudo permanece alinhado com a realidade biológica.',
    route: '/demo',
    selector: '[data-demo-tour="scope"]',
    duration: 7800
  },
  {
    id: 'command-status',
    eyebrow: '02 · Centro de comando',
    title: 'O estado da exploração num só gesto',
    body: 'Alertas, validações, sincronização e saúde do kit deixam de viver em silos.',
    narration: 'Isto não é apenas um dashboard. É o centro de comando da exploração. Alertas, validações, sincronização e saúde do kit. Tudo funciona, em conjunto.',
    route: '/demo',
    selector: '[data-demo-tour="command-status"]',
    duration: 7600
  },
  {
    id: 'latest-analysis',
    eyebrow: '03 · Visão computacional',
    title: 'Cada captura transforma-se em decisão',
    body: 'A análise visual entra diretamente no contexto do tanque, da espécie e da fase produtiva.',
    narration: 'Cada captura transforma-se numa decisão. A análise visual entra, diretamente, no contexto do tanque, da espécie e da fase produtiva.',
    route: '/demo',
    selector: '[data-demo-tour="latest-analysis"]',
    duration: 7000
  },
  {
    id: 'open-live-camera',
    eyebrow: '04 · Entrada em direto',
    title: 'Vamos entrar dentro do tanque',
    body: 'A experiência abre a câmara ao vivo para acompanhar a operação em tempo real.',
    narration: 'Agora, entramos dentro do tanque. A experiência abre a câmara ao vivo, para acompanhar a operação, em tempo real.',
    route: '/demo',
    selector: '[data-demo-tour="nav-live"]',
    actionSelector: '[data-demo-tour="nav-live"]',
    duration: 5200
  },
  {
    id: 'live-camera',
    eyebrow: '05 · Unidades',
    title: 'Visão contínua, com significado biológico',
    body: 'A IA acompanha os espécimes, destaca suspeitas e preserva o momento exato para revisão.',
    narration: 'A visão é contínua, mas nunca vazia. A inteligência artificial acompanha os espécimes, destaca suspeitas e preserva o momento certo, para revisão.',
    route: '/demo',
    selector: '[data-demo-tour="live-camera"]',
    duration: 7600
  },
  {
    id: 'ultrasound',
    eyebrow: '06 · Fusão sensorial',
    title: 'A câmara ganha profundidade',
    body: 'O sonar cruza ecos, densidade e contagem com a leitura visual para reduzir pontos cegos.',
    narration: 'A câmara ganha profundidade. O sonar cruza ecos, densidade e contagem, com a leitura visual. Assim, reduzimos os pontos cegos.',
    route: '/demo',
    selector: '[data-demo-tour="ultrasound"]',
    duration: 7200
  },
  {
    id: 'environment',
    eyebrow: '07 · Ambiente',
    title: 'A água também fala',
    body: 'Temperatura, pH, oxigénio dissolvido e salinidade formam uma rede de sinais em tempo real.',
    narration: 'A água, também fala. Temperatura, pê agá, oxigénio dissolvido e salinidade. Uma rede de sinais, em tempo real.',
    route: '/demo',
    selector: '[data-demo-tour="environment-sensors"]',
    duration: 6900
  },
  {
    id: 'simulate-response',
    eyebrow: '08 · Resposta ativa',
    title: 'Vamos provocar uma queda de oxigénio',
    body: 'O sistema reage ao evento, atualiza os sinais e converte desvio ambiental em ação operacional.',
    narration: 'Vamos provocar, agora, uma queda de oxigénio. O sistema reage ao evento, atualiza os sinais e transforma um desvio ambiental, numa ação operacional.',
    route: '/demo',
    selector: '[data-demo-tour="simulation-controls"]',
    actionSelector: '[aria-label="Simular queda O₂"]',
    duration: 6700
  },
  {
    id: 'live-alert',
    eyebrow: '09 · Alerta acionável',
    title: 'O sinal já chegou à operação',
    body: 'O evento não termina num gráfico: ganha prioridade, contexto e caminho de validação.',
    narration: 'O sinal já chegou à operação. O evento não termina num gráfico. Ganha prioridade, contexto e um caminho claro, de validação.',
    route: '/demo',
    selector: '[data-demo-tour="live-alerts"]',
    duration: 6500
  },
  {
    id: 'return-dashboard',
    eyebrow: '10 · Ciclo fechado',
    title: 'Voltamos ao centro de comando',
    body: 'A navegação preserva o fio da decisão e devolve-nos à visão global.',
    narration: 'Voltamos ao centro de comando. A navegação preserva o fio da decisão e devolve-nos, à visão global.',
    route: '/demo',
    selector: '[data-demo-tour="nav-operations"]',
    actionSelector: '[data-demo-tour="nav-operations"]',
    duration: 4800
  },
  {
    id: 'open-clinical',
    eyebrow: '11 · Validação',
    title: 'Abrimos a fila clínica',
    body: 'Os eventos com significado seguem para confirmação humana e aprendizagem contínua.',
    narration: 'Abrimos a fila clínica. Os eventos com significado seguem para confirmação humana e, depois, para aprendizagem contínua.',
    route: '/demo',
    selector: '[data-demo-tour="nav-clinical"]',
    actionSelector: '[data-demo-tour="nav-clinical"]',
    duration: 5300
  },
  {
    id: 'clinical-queue',
    eyebrow: '12 · Triagem inteligente',
    title: 'Prioridade antes de ruído',
    body: 'Filtros, severidade e estado organizam o trabalho da equipa com rastreabilidade.',
    narration: 'Prioridade, antes de ruído. Filtros, severidade e estado organizam o trabalho da equipa, com rastreabilidade.',
    route: '/demo/alerts',
    selector: '[data-demo-tour="clinical-queue"]',
    duration: 6600
  },
  {
    id: 'clinical-card',
    eyebrow: '13 · Conhecimento acumulado',
    title: 'Cada validação melhora a próxima decisão',
    body: 'A equipa confirma, rejeita ou investiga. O histórico torna-se memória operacional e dados de treino.',
    narration: 'Cada validação melhora a próxima decisão. A equipa confirma, rejeita ou investiga. E o histórico torna-se memória operacional, e dados de treino.',
    route: '/demo/alerts',
    selector: '[data-demo-tour="clinical-card"]',
    duration: 7600
  },
  {
    id: 'open-analytics',
    eyebrow: '14 · Inteligência estratégica',
    title: 'Da operação para o impacto',
    body: 'Vamos abrir a camada analítica para medir estabilidade, qualidade e retorno.',
    narration: 'Da operação, para o impacto. Vamos abrir a camada analítica, para medir estabilidade, qualidade e retorno.',
    route: '/demo/alerts',
    selector: '[data-demo-tour="nav-analytics"]',
    actionSelector: '[data-demo-tour="nav-analytics"]',
    duration: 5200
  },
  {
    id: 'analytics',
    eyebrow: '15 · Analytics',
    title: 'A inteligência prova o seu valor',
    body: 'Tendências e indicadores transformam dados técnicos em decisões de negócio.',
    narration: 'A inteligência prova o seu valor. Tendências e indicadores transformam dados técnicos, em decisões de negócio.',
    route: '/demo/alerts',
    selector: '[data-demo-tour="analytics"]',
    duration: 6900
  },
  {
    id: 'open-growth',
    eyebrow: '16 · Crescimento',
    title: 'Biomassa também faz parte do sistema',
    body: 'Acompanhamos crescimento, qualidade de medição e calibração por coorte.',
    narration: 'A biomassa, também faz parte do sistema. Acompanhamos crescimento, qualidade de medição e calibração, por coorte.',
    route: '/demo/alerts',
    selector: '[data-demo-tour="nav-growth"]',
    actionSelector: '[data-demo-tour="nav-growth"]',
    duration: 5200
  },
  {
    id: 'growth',
    eyebrow: '17 · Crescimento mensurável',
    title: 'O ciclo produtivo fica visível',
    body: 'O sistema liga visão, medições e evolução de biomassa numa leitura operacional contínua.',
    narration: 'O ciclo produtivo fica visível. O sistema liga visão, medições e evolução de biomassa, numa leitura operacional contínua.',
    route: '/demo/alerts',
    selector: '[data-demo-tour="growth"]',
    duration: 7200
  },
  {
    id: 'open-assistant',
    eyebrow: '18 · Assistente contextual',
    title: 'A operação pode conversar consigo',
    body: 'O Marine Assistant conhece a página, o tanque, os alertas e o contexto biológico atual.',
    narration: 'A operação pode conversar consigo. O Marine Assistant conhece a página, o tanque, os alertas e o contexto biológico, deste momento.',
    route: '/demo/alerts',
    selector: '[data-demo-tour="marine-assistant"]',
    actionSelector: '[data-demo-tour="marine-assistant"]',
    duration: 6200
  },
  {
    id: 'assistant-panel',
    eyebrow: '19 · Aquaculture OS',
    title: 'Dados que se tornam resposta',
    body: 'AQL Vision reúne observação, ambiente, validação, crescimento e inteligência assistida numa só operação.',
    narration: 'Dados tornam-se resposta. AQL Vision reúne observação, ambiente, validação, crescimento e inteligência assistida. Tudo, numa só operação.',
    route: '/demo/alerts',
    selector: '[data-demo-tour="marine-assistant-panel"]',
    duration: 8500
  }
];

const voiceScore = (voice: SpeechSynthesisVoice) => {
  const name = voice.name.toLowerCase();
  const language = voice.lang.toLowerCase();
  const femaleScore = FEMALE_VOICE_HINTS.some((hint) => name.includes(hint)) ? 90 : 0;
  const naturalScore = NATURAL_VOICE_HINTS.some((hint) => name.includes(hint)) ? 120 : 0;
  const localeScore = language === 'pt-pt' ? 70 : language.startsWith('pt') ? 45 : 0;
  const localScore = voice.localService ? 8 : 0;
  return naturalScore + femaleScore + localeScore + localScore;
};

const getFemaleVoice = (voices: SpeechSynthesisVoice[]) => {
  const portugueseVoices = voices.filter((voice) => voice.lang.toLowerCase().startsWith('pt'));
  const candidates = portugueseVoices.length ? portugueseVoices : voices;
  return [...candidates].sort((left, right) => voiceScore(right) - voiceScore(left))[0] ?? null;
};

const estimateNarrationDuration = (text: string) => {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const pauses = (text.match(/[,.!?;:]/g) ?? []).length;
  return Math.max(9000, words * 620 + pauses * 320);
};

const closeAssistant = () => {
  const panel = document.querySelector<HTMLElement>('[data-demo-tour="marine-assistant-panel"]');
  if (!panel) return;
  document.querySelector<HTMLButtonElement>('[aria-label="Close assistant"]')?.click();
};

export function DemoExperienceTour() {
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutsRef = useRef<number[]>([]);
  const advancingRef = useRef(false);
  const [phase, setPhase] = useState<TourPhase>('intro');
  const [stepIndex, setStepIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [narrationCompletedStepId, setNarrationCompletedStepId] = useState<string | null>(null);
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const [cursorClicking, setCursorClicking] = useState(false);
  const currentStep = TOUR_STEPS[stepIndex];
  const selectedVoice = useMemo(() => getFemaleVoice(voices), [voices]);
  const speechSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const clearScheduledActions = useCallback(() => {
    timeoutsRef.current.forEach((timer) => window.clearTimeout(timer));
    timeoutsRef.current = [];
    advancingRef.current = false;
    setCursorClicking(false);
  }, []);

  const schedule = useCallback((callback: () => void, delay: number) => {
    const timer = window.setTimeout(callback, delay);
    timeoutsRef.current.push(timer);
    return timer;
  }, []);

  useEffect(() => {
    if (!('speechSynthesis' in window)) return;
    const loadVoices = () => setVoices(window.speechSynthesis.getVoices());
    loadVoices();
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
  }, []);

  useEffect(() => {
    setNarrationCompletedStepId(null);

    if (phase !== 'touring' || !currentStep || !speechSupported) return;

    const synthesis = window.speechSynthesis;
    let active = true;
    synthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(currentStep.narration);
    if (selectedVoice) utterance.voice = selectedVoice;
    utterance.lang = selectedVoice?.lang ?? 'pt-PT';
    utterance.rate = 0.84;
    utterance.pitch = 1.02;
    utterance.volume = 0.96;

    utterance.onend = () => {
      if (!active) return;
      setNarrationCompletedStepId(currentStep.id);
    };
    utterance.onerror = (event) => {
      if (!active || event.error === 'interrupted' || event.error === 'canceled') return;
      setNarrationCompletedStepId(currentStep.id);
    };

    synthesis.speak(utterance);
    const safetyTimer = window.setTimeout(() => {
      if (!active) return;
      synthesis.cancel();
      setNarrationCompletedStepId(currentStep.id);
    }, estimateNarrationDuration(currentStep.narration) + 7000);

    return () => {
      active = false;
      window.clearTimeout(safetyTimer);
      synthesis.cancel();
    };
  }, [currentStep, phase, selectedVoice, speechSupported]);

  useEffect(() => {
    if (phase !== 'touring' || !currentStep?.route || location.pathname === currentStep.route) return;
    navigate(currentStep.route);
  }, [currentStep, location.pathname, navigate, phase]);

  useEffect(() => {
    if (phase !== 'touring' || !currentStep?.selector) {
      setSpotlight(null);
      return;
    }

    let disposed = false;
    let firstResolve = true;

    const updateSpotlight = () => {
      if (disposed) return;
      const element = document.querySelector<HTMLElement>(currentStep.selector!);
      if (!element) {
        setSpotlight(null);
        return;
      }

      if (firstResolve) {
        firstResolve = false;
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }

      const rect = element.getBoundingClientRect();
      const padding = 10;
      setSpotlight({
        left: Math.max(8, rect.left - padding),
        top: Math.max(8, rect.top - padding),
        width: Math.min(window.innerWidth - 16, rect.width + padding * 2),
        height: Math.min(window.innerHeight - 16, rect.height + padding * 2)
      });
    };

    const timer = window.setTimeout(updateSpotlight, 260);
    const interval = window.setInterval(updateSpotlight, 420);
    window.addEventListener('resize', updateSpotlight);
    window.addEventListener('scroll', updateSpotlight, true);

    return () => {
      disposed = true;
      window.clearTimeout(timer);
      window.clearInterval(interval);
      window.removeEventListener('resize', updateSpotlight);
      window.removeEventListener('scroll', updateSpotlight, true);
    };
  }, [currentStep, location.pathname, phase]);

  const finishTour = useCallback(() => {
    clearScheduledActions();
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setSpotlight(null);
    setPhase('complete');
  }, [clearScheduledActions]);

  const goToNextStep = useCallback(() => {
    if (phase !== 'touring' || !currentStep || advancingRef.current) return;
    advancingRef.current = true;

    const next = () => {
      if (stepIndex >= TOUR_STEPS.length - 1) {
        finishTour();
        return;
      }
      setStepIndex((value) => value + 1);
      advancingRef.current = false;
    };

    if (!currentStep.actionSelector) {
      next();
      return;
    }

    const target = document.querySelector<HTMLButtonElement>(currentStep.actionSelector);
    if (!target) {
      next();
      return;
    }

    setCursorClicking(true);
    schedule(() => target.click(), 360);
    schedule(() => {
      setCursorClicking(false);
      next();
    }, 920);
  }, [currentStep, finishTour, phase, schedule, stepIndex]);

  useEffect(() => {
    if (phase !== 'touring' || !autoplay || !currentStep) return;
    const waitForNarration = speechSupported;
    if (waitForNarration && narrationCompletedStepId !== currentStep.id) return;

    const timer = window.setTimeout(
      goToNextStep,
      waitForNarration ? STEP_BREATHING_PAUSE_MS : currentStep.duration ?? 6500
    );
    return () => window.clearTimeout(timer);
  }, [autoplay, currentStep, goToNextStep, narrationCompletedStepId, phase, speechSupported]);

  useEffect(() => () => {
    clearScheduledActions();
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  }, [clearScheduledActions]);

  const startTour = () => {
    clearScheduledActions();
    closeAssistant();
    setStepIndex(0);
    setAutoplay(true);
    setPhase('touring');
    navigate('/demo');
  };

  const closeTour = () => {
    clearScheduledActions();
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setSpotlight(null);
    setPhase('closed');
  };

  const previousStep = () => {
    if (stepIndex === 0) return;
    clearScheduledActions();
    setStepIndex((value) => Math.max(0, value - 1));
  };

  const toggleAutoplay = () => {
    setAutoplay((value) => {
      const nextValue = !value;
      if (speechSupported) {
        if (nextValue) {
          window.speechSynthesis.resume();
        } else {
          window.speechSynthesis.pause();
        }
      }
      return nextValue;
    });
  };

  const progress = useMemo(() => ((stepIndex + 1) / TOUR_STEPS.length) * 100, [stepIndex]);
  const spotlightStyle = spotlight ? ({
    left: spotlight.left,
    top: spotlight.top,
    width: spotlight.width,
    height: spotlight.height
  } satisfies CSSProperties) : undefined;
  const cursorStyle = spotlight ? ({
    left: spotlight.left + spotlight.width * 0.72,
    top: spotlight.top + Math.min(spotlight.height * 0.68, spotlight.height - 24)
  } satisfies CSSProperties) : undefined;

  return (
    <>
      <style>
        {`
          @keyframes aqlDemoMist {
            0%, 100% { transform: translate3d(-3%, -2%, 0) scale(1); opacity: 0.42; }
            50% { transform: translate3d(4%, 3%, 0) scale(1.12); opacity: 0.7; }
          }

          @keyframes aqlDemoSweep {
            0% { transform: translate3d(-120%, 0, 0) rotate(-9deg); opacity: 0; }
            28% { opacity: 0.42; }
            72% { opacity: 0.2; }
            100% { transform: translate3d(220%, 0, 0) rotate(-9deg); opacity: 0; }
          }

          @keyframes aqlDemoSpotlight {
            0% { transform: scale(0.965); border-color: rgba(103, 232, 249, 0.4); }
            50% { transform: scale(1.012); border-color: rgba(165, 243, 252, 0.96); }
            100% { transform: scale(1); border-color: rgba(103, 232, 249, 0.72); }
          }

          @keyframes aqlDemoCursorFloat {
            0%, 100% { transform: translate3d(-3px, -2px, 0) rotate(-12deg); }
            50% { transform: translate3d(6px, 7px, 0) rotate(-7deg); }
          }

          @keyframes aqlDemoCursorClick {
            0% { transform: scale(0.5); opacity: 0.9; }
            100% { transform: scale(2.3); opacity: 0; }
          }

          @keyframes aqlDemoReveal {
            from { opacity: 0; transform: translate3d(0, 18px, 0) scale(0.98); filter: blur(8px); }
            to { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
          }

          .aql-demo-mist { animation: aqlDemoMist 12s ease-in-out infinite; }
          .aql-demo-sweep { animation: aqlDemoSweep 7.5s ease-in-out infinite; }
          .aql-demo-spotlight { animation: aqlDemoSpotlight 1.2s cubic-bezier(0.22, 1, 0.36, 1) both; }
          .aql-demo-cursor { animation: aqlDemoCursorFloat 1.5s ease-in-out infinite; }
          .aql-demo-cursor-ping { animation: aqlDemoCursorClick 720ms ease-out infinite; }
          .aql-demo-reveal { animation: aqlDemoReveal 680ms cubic-bezier(0.22, 1, 0.36, 1) both; }

          @media (prefers-reduced-motion: reduce) {
            .aql-demo-mist,
            .aql-demo-sweep,
            .aql-demo-spotlight,
            .aql-demo-cursor,
            .aql-demo-cursor-ping,
            .aql-demo-reveal {
              animation: none !important;
            }
          }
        `}
      </style>

      {phase === 'intro' ? (
        <div className="fixed inset-0 z-[120] overflow-hidden bg-[#020914] text-white">
          <div className="aql-demo-mist pointer-events-none absolute -left-[16%] -top-[18%] h-[62vh] w-[62vh] rounded-full bg-cyan-400/18 blur-[130px]" />
          <div className="aql-demo-mist pointer-events-none absolute -bottom-[22%] -right-[14%] h-[68vh] w-[68vh] rounded-full bg-blue-600/18 blur-[150px]" style={{ animationDelay: '-4s' }} />
          <div className="pointer-events-none absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(125,211,252,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(125,211,252,0.08)_1px,transparent_1px)] [background-size:72px_72px]" />
          <div className="aql-demo-sweep pointer-events-none absolute inset-y-[-20%] left-[-12%] w-[42%] bg-gradient-to-r from-transparent via-cyan-200/14 to-transparent blur-2xl" />

          <div className="relative flex min-h-screen items-center justify-center px-5 py-10">
            <div className="aql-demo-reveal w-full max-w-4xl text-center">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[28px] border border-cyan-200/30 bg-cyan-300/10 text-cyan-100 shadow-[0_0_70px_rgba(34,211,238,0.22)] backdrop-blur">
                <Waves className="h-10 w-10" />
              </div>
              <p className="mt-8 text-xs font-bold uppercase tracking-[0.42em] text-cyan-200/78">AQL Vision · Demo Experience</p>
              <h1 className="mx-auto mt-5 max-w-4xl text-balance text-5xl font-black leading-[0.93] tracking-[-0.06em] text-white sm:text-7xl">
                MAIS DO QUE DASHBOARDS.
                <span className="block bg-gradient-to-r from-cyan-200 via-sky-300 to-emerald-200 bg-clip-text text-transparent">
                  UM AQUACULTURE OS.
                </span>
              </h1>
              <p className="mx-auto mt-7 max-w-2xl text-base leading-7 text-slate-300 sm:text-lg">
                Uma visita guiada sensorial pela operação: visão, sonar, ambiente, validação, crescimento e inteligência assistida.
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={startTour}
                  className="inline-flex items-center gap-2 rounded-2xl border border-cyan-100/45 bg-cyan-200 px-5 py-3 text-sm font-bold text-slate-950 shadow-[0_18px_40px_-18px_rgba(103,232,249,0.92)] transition hover:-translate-y-0.5 hover:bg-white"
                >
                  <Play className="h-4 w-4 fill-current" />
                  Iniciar experiência
                </button>
              </div>
              <p className="mt-5 text-xs uppercase tracking-[0.2em] text-slate-500">19 momentos · aproximadamente 2 minutos</p>
            </div>
          </div>
        </div>
      ) : null}

      {phase === 'touring' ? (
        <>
          <div className="pointer-events-none fixed inset-0 z-[110] overflow-hidden">
            <div className="aql-demo-mist absolute -left-32 -top-36 h-96 w-96 rounded-full bg-cyan-400/12 blur-[110px]" />
            <div className="aql-demo-sweep absolute inset-y-[-15%] left-[-16%] w-[34%] bg-gradient-to-r from-transparent via-cyan-200/10 to-transparent blur-3xl" />
          </div>

          {spotlight ? (
            <>
              <div
                key={currentStep.id}
                className="aql-demo-spotlight pointer-events-none fixed z-[112] rounded-[22px] border-2 shadow-[0_0_0_9999px_rgba(2,6,23,0.62),0_0_0_7px_rgba(34,211,238,0.12),0_0_54px_rgba(34,211,238,0.3)]"
                style={spotlightStyle}
              />
              <div
                className="aql-demo-cursor pointer-events-none fixed z-[116] text-cyan-100 drop-shadow-[0_8px_18px_rgba(8,145,178,0.72)]"
                style={cursorStyle}
              >
                {cursorClicking ? <span className="aql-demo-cursor-ping absolute -left-3 -top-3 h-10 w-10 rounded-full border-2 border-cyan-200" /> : null}
                <MousePointer2 className="h-8 w-8 fill-slate-950/55" />
              </div>
            </>
          ) : (
            <div className="pointer-events-none fixed inset-0 z-[112] bg-slate-950/58" />
          )}

          <div className="fixed left-1/2 top-4 z-[118] flex -translate-x-1/2 items-center gap-2 rounded-full border border-cyan-200/24 bg-slate-950/82 px-3 py-2 text-xs font-semibold text-cyan-50 shadow-lg backdrop-blur-xl">
            <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-300" />
            AQL Vision Experience
            <span className="text-cyan-200/58">{stepIndex + 1}/{TOUR_STEPS.length}</span>
          </div>

          <aside key={currentStep.id} className="aql-demo-reveal fixed inset-x-3 bottom-3 z-[118] mx-auto max-w-2xl rounded-[24px] border border-cyan-200/24 bg-[#071323]/94 p-4 text-white shadow-[0_24px_74px_rgba(2,6,23,0.62)] backdrop-blur-xl sm:bottom-5 sm:p-5">
            <div className="h-1 overflow-hidden rounded-full bg-white/8">
              <div className="h-full rounded-full bg-gradient-to-r from-cyan-300 via-sky-300 to-emerald-300 transition-all duration-700" style={{ width: `${progress}%` }} />
            </div>
            <div className="mt-4 flex gap-4">
              <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-cyan-200/20 bg-cyan-300/10 text-cyan-200 sm:flex">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-cyan-200/70">{currentStep.eyebrow}</p>
                <h2 className="mt-1 text-xl font-bold tracking-tight text-white">{currentStep.title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-300">{currentStep.body}</p>
              </div>
              <button
                type="button"
                onClick={closeTour}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-slate-400 transition hover:bg-white/[0.1] hover:text-white"
                aria-label="Fechar visita guiada"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleAutoplay}
                  className="inline-flex h-9 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 text-xs font-semibold text-cyan-50 transition hover:bg-white/[0.1]"
                >
                  {autoplay ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                  {autoplay ? 'Pausar' : 'Continuar'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={previousStep}
                  disabled={stepIndex === 0}
                  className="inline-flex h-9 items-center gap-1 rounded-xl border border-white/10 bg-white/[0.04] px-3 text-xs font-semibold text-slate-200 transition hover:bg-white/[0.1] disabled:pointer-events-none disabled:opacity-35"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={goToNextStep}
                  className="inline-flex h-9 items-center gap-1 rounded-xl border border-cyan-100/38 bg-cyan-200 px-3 text-xs font-bold text-slate-950 transition hover:bg-white"
                >
                  {stepIndex === TOUR_STEPS.length - 1 ? 'Concluir' : 'Seguinte'}
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </aside>
        </>
      ) : null}

      {phase === 'complete' ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center overflow-hidden bg-[#020914]/92 px-5 py-10 text-white backdrop-blur-xl">
          <div className="aql-demo-mist pointer-events-none absolute -right-24 top-8 h-[28rem] w-[28rem] rounded-full bg-cyan-400/20 blur-[120px]" />
          <div className="aql-demo-reveal relative w-full max-w-3xl rounded-[32px] border border-cyan-200/24 bg-slate-950/68 p-6 text-center shadow-[0_32px_90px_rgba(2,6,23,0.58)] sm:p-10">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[22px] border border-cyan-200/28 bg-cyan-300/10 text-cyan-100">
              <Sparkles className="h-7 w-7" />
            </div>
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.32em] text-cyan-200/74">AQL Vision</p>
            <h2 className="mt-3 text-4xl font-black tracking-[-0.06em] text-white sm:text-6xl">AQUACULTURE OS</h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-7 text-slate-300">
              Uma operação conectada, sensorial e inteligente: da água à decisão, num único sistema vivo.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={startTour}
                className="inline-flex items-center gap-2 rounded-2xl border border-cyan-100/42 bg-cyan-200 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-white"
              >
                <RotateCcw className="h-4 w-4" />
                Repetir experiência
              </button>
              <button
                type="button"
                onClick={closeTour}
                className="inline-flex items-center gap-2 rounded-2xl border border-white/14 bg-white/[0.05] px-5 py-3 text-sm font-semibold text-slate-200 transition hover:bg-white/[0.1] hover:text-white"
              >
                Explorar livremente
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {phase === 'closed' ? (
        <button
          type="button"
          onClick={() => setPhase('intro')}
          className="fixed bottom-5 left-5 z-[78] inline-flex items-center gap-2 rounded-2xl border border-cyan-200/30 bg-slate-950/92 px-4 py-3 text-xs font-bold text-cyan-100 shadow-[0_16px_36px_rgba(2,6,23,0.28)] backdrop-blur transition hover:-translate-y-0.5 hover:border-cyan-200/55 hover:bg-cyan-950"
        >
          <Sparkles className="h-4 w-4" />
          Repetir visita guiada
        </button>
      ) : null}
    </>
  );
}
